// Carry-terms domain helpers for the Interest Modeling carry editor (INS-10673).
// Pure, dependency-free — the trigger catalog (labels + slider
// ranges/steps/defaults/units), the derived-direction rule, and the fresh-group
// factory. The FE only CAPTURES these terms into the working config; the carry
// cashflow math lives in the calculator (BE-calc, deferred).
//
// The trigger ranges/steps/defaults are ported verbatim from the PM mock
// (carried-interest-modeling v5 `model.ts` `TRIGGERS`), the interaction truth.
import type {
  CarryDirection,
  CarryGroup,
  CarryTrigger,
  CarryWell,
  InterestItem,
} from './types';

/** One payout-trigger's display + control metadata. */
export interface CarryTriggerMeta {
  id: CarryTrigger;
  /** Display label in the payout-metric dropdown. */
  label: string;
  /** Whether the trigger takes a threshold value (false ⇒ no slider, e.g. At First Production). */
  hasValue: boolean;
  /** Threshold unit shown on the slider label (absent for `firstProd`). */
  unit?: string;
  /** Slider min / max / step / default (absent for `firstProd`). */
  min?: number;
  max?: number;
  step?: number;
  default?: number;
}

/**
 * The seven carry payout triggers with their slider ranges/steps/defaults/units
 * Order is the dropdown order. Ranges match the PM mock exactly.
 */
/**
 * The payout-account bases (INS-10673). A recovery trigger states a multiple OF
 * something, and the two conventions differ by roughly 34 points of effective
 * penalty on the same wells — so the basis is chosen, never assumed.
 */
export const PAYOUT_BASES = [
  {
    id: 'netProceeds' as const,
    label: 'Net proceeds',
    help: 'Revenue after royalty, production and ad valorem taxes, less operating costs — the JOA recoupment convention.',
  },
  {
    id: 'grossRevenue' as const,
    label: 'Gross revenue',
    help: 'Revenue at the relinquished share, with nothing deducted — a simple, auditable hurdle.',
  },
];

/** The payout basis ids. */
export type PayoutBasis = (typeof PAYOUT_BASES)[number]['id'];

/** The basis a position takes when none is stated. */
export const DEFAULT_PAYOUT_BASIS: PayoutBasis = 'netProceeds';

/**
 * Whether a trigger opens a payout account at all — only these measure a multiple
 * against a stream, so only these have a basis to state.
 *
 * @param {CarryTrigger} [trigger] - The trigger id.
 * @return {boolean} True for costRecovery / moi / ror.
 */
export function triggerHasBasis(trigger?: CarryTrigger): boolean {
  return trigger === 'costRecovery' || trigger === 'moi' || trigger === 'ror';
}

export const CARRY_TRIGGERS: CarryTriggerMeta[] = [
  { id: 'firstProd', label: 'At First Production', hasValue: false },
  {
    id: 'daysAfter',
    label: 'Days After First Production',
    hasValue: true,
    unit: 'days',
    min: 0,
    max: 1095,
    step: 30,
    default: 90,
  },
  {
    id: 'costRecovery',
    label: 'Cost Recovery (+ interest)',
    hasValue: true,
    unit: '% interest',
    min: 0,
    max: 30,
    step: 0.5,
    default: 10,
  },
  {
    id: 'moi',
    label: 'Multiple of Investment',
    hasValue: true,
    unit: '%',
    min: 100,
    max: 500,
    step: 5,
    default: 200,
  },
  {
    id: 'ror',
    label: 'Rate of Return Hurdle',
    hasValue: true,
    unit: '%',
    min: 0,
    max: 40,
    step: 0.5,
    default: 15,
  },
  {
    id: 'grossRevenue',
    label: 'Gross Revenue',
    hasValue: true,
    unit: '$mm',
    min: 5,
    max: 80,
    step: 1,
    default: 25,
  },
  {
    id: 'volume',
    label: 'Cumulative Volume',
    hasValue: true,
    unit: 'mboe',
    min: 100,
    max: 2000,
    step: 25,
    default: 600,
  },
];

/** Trigger id → its metadata. */
export const CARRY_TRIGGER_BY_ID = Object.fromEntries(
  CARRY_TRIGGERS.map((meta) => [meta.id, meta])
) as Record<CarryTrigger, CarryTriggerMeta>;

/**
 * The threshold a trigger resets to on selection: its `default`, or 0 for
 * a value-less trigger (`firstProd`). The single source for the reset so the
 * dropdown and the seed agree.
 *
 * @param {CarryTrigger} trigger - The selected trigger.
 * @return {number} The threshold to snap to.
 */
export function thresholdForTrigger(trigger: CarryTrigger): number {
  return CARRY_TRIGGER_BY_ID[trigger].default ?? 0;
}

/**
 * The derived carry direction — never entered. BPO WI below APO WI means
 * our interest reverts IN at payout (another party is carrying us → `carried`);
 * otherwise we hold extra up front and give it back (`carrying`). A flat
 * BPO === APO position reads as `carrying` (no reversal).
 *
 * @param {number} bpoWi - Before-payout working interest (%).
 * @param {number} apoWi - After-payout working interest (%).
 * @return {CarryDirection | null} The derived direction, or null while the APO half is unset.
 */
export function deriveCarryDirection(
  bpoWi: number,
  apoWi: number | null
): CarryDirection | null {
  if (apoWi === null) {
    return null;
  }
  return bpoWi < apoWi ? 'carried' : 'carrying';
}

/**
 * The terms a fresh group opens on. BPO keeps the "carrying" preset (100/75 — you
 * carry someone before payout, which is a position, not a measurement). The APO half
 * is NULL: it is our actual interest in the group's properties, and an empty group
 * has none to read, so the fields open EMPTY rather than showing a plausible figure
 * nobody chose (INS-10673). They fill in from the properties on the first pick.
 */
export const DEFAULT_CARRY_TERMS = {
  bpoWi: 100,
  bpoNri: 75,
  apoWi: null,
  apoNri: null,
} as const;

/** The trigger a fresh group opens on. */
export const DEFAULT_CARRY_TRIGGER: CarryTrigger = 'moi';

/**
 * Builds a fresh carry group with the default interest split + payout metric and
 * the given wells. A brand-new, stable id (`crypto.randomUUID()`) so the group is
 * diffable across edits.
 *
 * @param {string} name - The group label (e.g. "Group 1").
 * @param {string[]} [wellIds] - Wells to seed the group with (default none).
 * @param {CarryApoTerms | null} [terms] - Our actual interest in those wells, overriding the preset's APO half.
 * @return {CarryGroup} The new group.
 */
export function makeCarryGroup(
  name: string,
  wellIds: string[] = [],
  terms?: CarryApoTerms | null
): CarryGroup {
  return {
    id: crypto.randomUUID(),
    name,
    wellIds,
    ...DEFAULT_CARRY_TERMS,
    // Our real interest in the group's wells wins over the BPO/APO preset's APO
    // half. Absent (mixed or unrated wells) ⇒ the preset stands, and the editor
    // shows the mismatch rather than inventing a rate.
    ...(terms ?? {}),
    trigger: DEFAULT_CARRY_TRIGGER,
    threshold: thresholdForTrigger(DEFAULT_CARRY_TRIGGER),
  };
}

/**
 * Whether a carry item holds a group that has properties but no after-payout terms
 * — the state a group lands in when its properties carry no certified interest, so
 * nothing could be derived and the fields stayed blank.
 *
 * An EMPTY group is fine and is ignored here: it prices nothing, and
 * `isCarryItemComplete` skips it too. This is the Calculate gate's check, because
 * carries otherwise never gate, and a null APO would reach the calculator as a
 * position with no interest attached.
 *
 * @param {InterestItem} item - The interest item.
 * @return {boolean} True when a populated group is missing its APO terms.
 */
export function carryTermsMissing(item: InterestItem): boolean {
  return (
    item.kind === 'carry' &&
    (item.groups ?? []).some(
      (group) =>
        group.wellIds.length > 0 &&
        (group.apoWi === null || group.apoNri === null)
    )
  );
}

// ── Well interest → group terms (INS-10673) ───────────────────────────────────
// A carry group prices ONE interest: every well in it must share our actual
// (NRI, WI). These helpers derive a group's APO terms from its wells and split a
// tract's roster into the cohorts that may legally share a group.

/** The APO half of a group's terms — our actual interest in its wells, as percents. */
export interface CarryApoTerms {
  apoWi: number;
  apoNri: number;
}

/**
 * Decimal places a well's interest is compared and displayed at. The certified
 * allocation carries more precision than anyone reads (0.09668750), and raw float
 * equality would split a cohort on noise far below what the UI can show.
 */
const RATE_DP = 6;

/**
 * A well's interest as a percent, rounded to {@link RATE_DP}. `null` when the
 * well carries no figure — never defaulted to 0, which would read as "we own none"
 * rather than "unknown".
 *
 * @param {number | null} [fraction] - The interest as a fraction (0–1).
 * @return {number | null} The percent, or null when absent.
 */
export function interestPct(fraction?: number | null): number | null {
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) {
    return null;
  }
  return Number((fraction * 100).toFixed(RATE_DP));
}

/**
 * The identity of a well's interest — two wells may share a group only when this
 * matches. `null` when either side is unknown: an unrated well has no identity, so
 * it can neither seed terms nor be proven compatible.
 *
 * @param {CarryWell} well - The roster well.
 * @return {string | null} The rate key, or null when the well carries no rate.
 */
export function wellRateKey(well: CarryWell): string | null {
  const nri = interestPct(well.nri);
  const wi = interestPct(well.wi);
  if (nri === null || wi === null) {
    return null;
  }
  return `${nri}|${wi}`;
}

/**
 * The APO terms a set of wells implies: our actual interest when they all share
 * one rate. `null` when the set is empty, carries an unrated well, or spans more
 * than one rate — the three cases where no single honest number exists.
 *
 * @param {CarryWell[]} wells - The wells to derive from.
 * @return {CarryApoTerms | null} The terms, or null when undefined.
 */
export function carryTermsFromWells(wells: CarryWell[]): CarryApoTerms | null {
  if (wells.length === 0) {
    return null;
  }
  const keys = new Set(wells.map(wellRateKey));
  if (keys.size !== 1 || keys.has(null)) {
    return null;
  }
  const [first] = wells;
  const apoNri = interestPct(first.nri);
  const apoWi = interestPct(first.wi);
  return apoNri === null || apoWi === null ? null : { apoWi, apoNri };
}

/**
 * Each well's cohort index — its position in {@link splitWellsByInterest}'s output.
 * The picker reads this to ORDER and COLOUR the menu by interest, and to work out
 * which rows to deactivate once a group has committed to a rate. Wells absent from
 * the roster have no index (the caller decides what that means).
 *
 * @param {CarryWell[]} wells - The tract's roster.
 * @return {Map<string, number>} well id → cohort index.
 */
export function cohortIndexByWell(wells: CarryWell[]): Map<string, number> {
  const index = new Map<string, number>();
  splitWellsByInterest(wells).forEach((cohort, position) => {
    for (const well of cohort) {
      index.set(well.id, position);
    }
  });
  return index;
}

/**
 * Splits a tract's wells into the cohorts that may share a group — one per
 * distinct rate, in first-seen roster order so the grouping is stable across
 * renders. Unrated wells collect into their own trailing cohort (they share no
 * provable rate, so they cannot join a rated group).
 *
 * @param {CarryWell[]} wells - The tract's roster.
 * @return {Array<CarryWell[]>} The cohorts, each internally homogeneous.
 */
export function splitWellsByInterest(wells: CarryWell[]): CarryWell[][] {
  const byKey = new Map<string, CarryWell[]>();
  const unrated: CarryWell[] = [];
  for (const well of wells) {
    const key = wellRateKey(well);
    if (key === null) {
      unrated.push(well);
      continue;
    }
    const cohort = byKey.get(key);
    if (cohort) {
      cohort.push(well);
    } else {
      byKey.set(key, [well]);
    }
  }
  const cohorts = [...byKey.values()];
  if (unrated.length > 0) {
    cohorts.push(unrated);
  }
  return cohorts;
}
